import * as React from 'react';
import type { ComponentType, SVGProps } from 'react';
import ArrowBack from '../../../components/Icons/ArrowBack';
import ArrowDownward from '../../../components/Icons/ArrowDownward';
import ArrowForward from '../../../components/Icons/ArrowForward';
import ArrowForwardMaterial from '../../../components/Icons/ArrowForwardMaterial';
import ArrowForwardIosSharp from '../../../components/Icons/ArrowForwardIosSharp';
import ArrowOutline from '../../../components/Icons/ArrowOutline';
import Check from '../../../components/Icons/Check';
import CheckCircle from '../../../components/Icons/CheckCircle';
import ChevronRight from '../../../components/Icons/ChevronRight';
import Close from '../../../components/Icons/Close';
import Email from '../../../components/Icons/Email';
import Facebook from '../../../components/Icons/Facebook';
import FiberManualRecord from '../../../components/Icons/FiberManualRecord';
import FiberManualRecordOutlined from '../../../components/Icons/FiberManualRecordOutlined';
import HighlightOff from '../../../components/Icons/HighlightOff';
import InfoOutlined from '../../../components/Icons/InfoOutlined';
import KeyboardArrowDown from '../../../components/Icons/KeyboardArrowDown';
import KeyboardArrowUp from '../../../components/Icons/KeyboardArrowUp';
import Language from '../../../components/Icons/Language';
import Lock from '../../../components/Icons/Lock';
import Menu from '../../../components/Icons/Menu';
import Twitter from '../../../components/Icons/Twitter';

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const iconRegistry: Record<string, IconComponent> = {
  'arrow-back': ArrowBack,
  'arrow-downward': ArrowDownward,
  'arrow-forward': ArrowForward,
  'arrow-forward-material': ArrowForwardMaterial,
  'arrow-forward-ios': ArrowForwardIosSharp,
  'arrow-forward-ios-sharp': ArrowForwardIosSharp,
  'arrow-outline': ArrowOutline,
  check: Check,
  'check-circle': CheckCircle,
  'chevron-right': ChevronRight,
  close: Close,
  email: Email,
  facebook: Facebook,
  'fiber-manual-record': FiberManualRecord,
  'fiber-manual-record-outlined': FiberManualRecordOutlined,
  'highlight-off': HighlightOff,
  'info-outlined': InfoOutlined,
  'keyboard-arrow-down': KeyboardArrowDown,
  'keyboard-arrow-up': KeyboardArrowUp,
  language: Language,
  lock: Lock,
  menu: Menu,
  twitter: Twitter,
};

export type GetIconOptions = {
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  'data-testid'?: string;
  svgProps?: React.SVGProps<SVGSVGElement>;
};

// Pass `ariaLabel` for icon-only contexts (announces the icon); omit for decorative icons
// alongside text (hidden from the a11y tree).
const getIcon = (icon: string, options?: GetIconOptions | string): React.ReactNode => {
  const opts: GetIconOptions = typeof options === 'string' ? { ariaLabel: options } : (options ?? {});
  const key = icon.toLowerCase();
  const a11yProps = opts.ariaLabel
    ? { role: 'img' as const, 'aria-label': opts.ariaLabel }
    : { 'aria-hidden': true as const };
  const CustomIcon = iconRegistry[key];

  if (!CustomIcon) {
    return null;
  }

  return (
    <span
      {...a11yProps}
      className={opts.className}
      style={{ display: 'inline-flex', ...opts.style }}
      data-testid={opts['data-testid']}
    >
      <CustomIcon {...opts.svgProps} />
    </span>
  );
};

export default getIcon;
